import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { launchCashfree } from "@/lib/cashfreeCheckout";
import PayMethodPicker from "@/components/PayMethodPicker";
import { useAuth } from "@/context/AuthContext";
import { sellerHome } from "@/lib/onboarding";
import { toast } from "sonner";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function Subscribe() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [cashfreeReady, setCashfreeReady] = useState(true);
  const [payment, setPayment] = useState(null);
  const [provider, setProvider] = useState("");
  const [interval, setInterval] = useState("month");
  const [planId, setPlanId] = useState("pro");
  const [phone, setPhone] = useState(user?.profile?.address_phone || "");
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/seller/billing/plans");
        if (cancelled) return;
        setPlans(data.plans || []);
        setSub(data.subscription);
        setCashfreeReady(data.cashfree_configured !== false);
        setPayment(data.payment || null);
        if (data.payment?.recommended) setProvider(data.payment.recommended);
        const status = data.subscription?.status;
        const unpaid = status === "required" || status === "past_due";
        if (!unpaid && data.subscription?.access) {
          const latest = await refreshUser();
          navigate(sellerHome(latest || user), { replace: true });
        }
      } catch (e) {
        toast.error(apiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, refreshUser]);

  const pay = async () => {
    setPaying(true);
    try {
      if (provider === "cashfree" && !String(phone || "").trim()) {
        toast.error("Enter a mobile number. Cashfree needs it for UPI and Indian cards.");
        setPaying(false);
        return;
      }
      const { data } = await api.post("/seller/billing/checkout", {
        plan_id: planId,
        interval,
        phone,
        provider,
        origin_url: window.location.origin,
      });
      if (data.payment_session_id) await launchCashfree(data);
      else window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e));
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-aura-muted">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 pt-16 pb-24" data-testid="seller-subscribe-page">
      <div className="text-[11px] tracking-luxe uppercase text-aura-cyan mb-3">Seller plans</div>
      <h1 className="font-serif text-4xl font-bold tracking-tight mb-3">Start selling on Shoptosa</h1>
      <p className="text-aura-muted max-w-xl mb-8">
        Shoppers register free. Sellers pick a plan, then pay with Cashfree from India or Stripe from anywhere else.
      </p>
      {!cashfreeReady && (
        <p className="text-sm border border-aura-border bg-aura-surface px-4 py-3 mb-8" data-testid="cashfree-missing">
          Cashfree keys are not saved yet. In Admin → Settings → Payments, paste App ID and secret, set sandbox to false for live keys, then return here to pay.
        </p>
      )}

      <div className="inline-flex border border-aura-border rounded-full p-1 mb-8">
        {[
          { id: "month", label: "Monthly" },
          { id: "year", label: "Yearly · 2 months free" },
        ].map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => setInterval(opt.id)}
            className={`px-4 py-2 text-sm rounded-full ${
              interval === opt.id ? "bg-aura-accent text-white" : "text-aura-muted hover:text-aura-ink"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        {plans.map((plan) => {
          const price = interval === "year" ? plan.yearly_inr : plan.monthly_inr;
          const selected = planId === plan.id;
          return (
            <button
              key={plan.id}
              type="button"
              onClick={() => setPlanId(plan.id)}
              data-testid={`plan-${plan.id}`}
              className={`text-left p-6 border transition-colors ${
                selected ? "border-aura-accent bg-aura-surface" : "border-aura-border hover:border-aura-accent/50"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="font-serif text-2xl">{plan.name}</div>
                {plan.recommended && <span className="text-[11px] uppercase tracking-wide text-aura-cyan">Recommended</span>}
              </div>
              <div className="text-3xl font-semibold mb-1">
                {inr(price)}
                <span className="text-sm font-normal text-aura-muted"> / {interval === "year" ? "year" : "month"}</span>
              </div>
              <p className="text-sm text-aura-muted mb-4">{plan.blurb}</p>
              <ul className="space-y-2">
                {(plan.features || []).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 mt-0.5 text-aura-accent shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </button>
          );
        })}
      </div>

      <div className="max-w-xl mb-6">
        <PayMethodPicker
          methods={payment?.methods}
          value={provider}
          onChange={setProvider}
          note={payment?.note}
        />
      </div>
      {provider === "cashfree" && (
        <>
          <label className="block text-sm mb-2">Mobile number for UPI / Cashfree</label>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="10-digit mobile"
            inputMode="tel"
            data-testid="subscribe-phone"
            className="w-full max-w-sm px-4 py-3 bg-aura-surface border border-aura-border focus:border-aura-accent outline-none text-sm mb-6"
          />
        </>
      )}

      <button
        type="button"
        disabled={paying || !provider}
        onClick={pay}
        data-testid="subscribe-pay"
        className="px-8 py-3.5 btn-accent rounded-full text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"
      >
        {paying && <Loader2 className="w-4 h-4 animate-spin" />}
        {provider === "stripe" ? "Pay with Stripe" : "Pay with Cashfree"}
      </button>
      {sub?.status === "past_due" && (
        <p className="text-sm text-aura-muted mt-4">Your previous period ended. Pay to reopen the studio.</p>
      )}
    </div>
  );
}
